/**
 * "Skip these paths" — the per-group picker, shared by formulas and rules.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Trigger styling follows RuleActionsSelect, with one deliberate inversion: the
 * warning treatment marks the CONFIGURED state, not the empty one. Ignoring
 * nothing is the normal case and should look calm; ignoring three paths is the
 * thing you want to notice without opening anything.
 */

import { FC } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, EyeOff } from 'lucide-react';

import { MultiSelect } from '@/components/ui';
import { cn } from '@/lib/utils';

import {
  offFlowIds,
  type IgnorePathOption,
} from './ignore-path-options';

interface Props {
  value: string[];
  options: IgnorePathOption[];
  onChange: (next: string[]) => void;
  /** What this group is, for the tooltip ("formula" | "rule"). */
  kind: 'formula' | 'rule';
  /**
   * False while the live preview has not produced this flow's paths yet.
   * Without it, every saved skip is reported as "not on this flow right now"
   * for as long as the preview takes — telling the user their setting is broken
   * when it is merely still loading.
   */
  optionsReady?: boolean;
}

const HELP = {
  formula:
    'Paths this formula does NOT score. They are judged by your other formulas instead; a path skipped by every formula keeps its current weight and is left out of the maths.',
  rule: 'Paths this rule never applies to. It will not match them, whatever they do.',
} as const;

export const IgnorePathsSelect: FC<Props> = ({
  value,
  options,
  onChange,
  kind,
  optionsReady,
}) => {
  const count = value.length;
  const ready = optionsReady !== false;
  const stranded = ready ? offFlowIds(value, options) : [];
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          title={HELP[kind]}
          className={cn(
            'inline-flex h-7 shrink-0 items-center gap-1 whitespace-nowrap rounded-md border px-2.5 text-[11px] font-medium transition-colors',
            count
              ? 'border-warning/50 bg-warning/10 text-warning hover:border-warning'
              : 'border-dashed border-border text-muted-foreground hover:border-primary/50 hover:text-primary',
          )}
        >
          <EyeOff className="h-3 w-3 shrink-0" aria-hidden />
          {count ? (
            <span className="uppercase tracking-wide text-warning/80">Skip</span>
          ) : null}
          <span>
            {count ? `${count} path${count === 1 ? '' : 's'}` : 'Skip paths'}
          </span>
          <ChevronDown
            className={cn(
              'h-3 w-3 shrink-0',
              count ? 'text-warning/70' : 'text-muted-foreground',
            )}
            aria-hidden
          />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          className="z-[100] w-[18rem] rounded-md border border-border bg-popover p-2 text-popover-foreground shadow-lg"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <p className="mb-1.5 text-[10.5px] leading-snug text-muted-foreground">
            {HELP[kind]}
          </p>
          <MultiSelect
            options={options}
            value={value}
            onChange={onChange}
            searchPlaceholder="Search paths…"
            emptyText={
              ready
                ? 'Only paths the bank knows can be skipped.'
                : "Still loading this flow's paths…"
            }
            maxRender={50}
            pinSelected
            bulkActions
          />
          {stranded.length > 0 ? (
            <p
              className="mt-1.5 text-[10px] leading-snug text-muted-foreground"
              title={stranded.join('\n')}
            >
              +{stranded.length} kept but not on this flow right now — they stay
              skipped if the path comes back.
            </p>
          ) : null}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default IgnorePathsSelect;
